import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useProjectObjectPermissions } from '~/modules/common/hooks/project';
import {
  RESOURCE_ALLOCATION_PERMISSION,
  RESOURCE_REQUEST_PERMISSION
} from '~/modules/common/enums';
import { useMeContext } from '~/modules/me/useMeContext';
import ProjectContext from './ProjectContext';

export const getResourceAllocationPermissions = ({
  permissionsMap,
  isPsaRmpEnhancedProjectManagerPersonaEnabled
}) => {
  const isCompletedResourceAllocationEditAllowed = Boolean(
    permissionsMap[
      RESOURCE_ALLOCATION_PERMISSION.WEBUI_EDIT_COMPLETE_RESOURCE_ALLOCATION
    ]
  );
  const canViewResourceAssignments = Boolean(
    permissionsMap[RESOURCE_REQUEST_PERMISSION.VIEW_RESOURCE_ASSIGNMNET]
  );
  const canViewResourceAllocationPermission = Boolean(
    permissionsMap[RESOURCE_ALLOCATION_PERMISSION.VIEW_RESOURCE_ALLOCATION]
  );
  const canEditResourceAllocationPermission = Boolean(
    permissionsMap[RESOURCE_ALLOCATION_PERMISSION.EDIT_RESOURCE_ALLOCATION]
  );

  return {
    canEditResourceAllocation:
      isCompletedResourceAllocationEditAllowed ||
      (isPsaRmpEnhancedProjectManagerPersonaEnabled &&
        canEditResourceAllocationPermission),
    canViewResourceAllocation: isPsaRmpEnhancedProjectManagerPersonaEnabled
      ? canViewResourceAssignments || canViewResourceAllocationPermission
      : true
  };
};

export const getProjectFromProps = ({ project }) => {
  if (!project) return null;

  return {
    ...project,
    hasStartDate: Boolean(project.startDate),
    hasEndDate: Boolean(project.endDate)
  };
};

const ProjectContextProvider = ({ children, project }) => {
  const projectObjectPermissions = useProjectObjectPermissions({ project });
  const {
    permissionsMap,
    featureFlags: { isPsaRmpEnhancedProjectManagerPersonaEnabled }
  } = useMeContext();

  const {
    canViewResourceAllocation,
    canEditResourceAllocation
  } = getResourceAllocationPermissions({
    permissionsMap,
    isPsaRmpEnhancedProjectManagerPersonaEnabled
  });

  const contextValues = useMemo(
    () => ({
      project: getProjectFromProps({ project }),
      projectObjectPermissions: {
        ...projectObjectPermissions,
        canViewResourceAllocation,
        canEditResourceAllocation
      }
    }),
    [
      project,
      projectObjectPermissions,
      canViewResourceAllocation,
      canEditResourceAllocation
    ]
  );

  return (
    <ProjectContext.Provider value={contextValues}>
      {children}
    </ProjectContext.Provider>
  );
};

ProjectContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  project: PropTypes.object
};

export default ProjectContextProvider;
